import { AxiosPromise, AxiosResponse } from 'axios';
import { apiService, manageFilters } from '@app/services';
import { IRoleMemberCount, IActiveFilter } from '@app/models';
import { manageSearchFilters } from './filters.service';

class RolesService {
    readonly apiBase: string = '/api/directory/Roles';

    getRoles(
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        organizationId: string,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(this.apiBase, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: true,
                    ...(filters ? { $filter: manageFilters(filters, organizationId) } : { $filter: manageFilters([], organizationId)})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getUsersNotInRole(
        roleId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/Roles(${roleId})/UsersNotInRole`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getUsersInRole(
        roleId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/Roles(${roleId})/UsersInRole`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupsNotInRole(
        roleId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/Roles(${roleId})/GroupsNotInRole`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupsInRole(
        roleId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/Roles(${roleId})/GroupsInRole`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageSearchFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    removeGroupsFromRole(roleId: string, groupsForRole: any[]): AxiosPromise {
        const apiUrl: string = `/api/directory/Roles(${roleId})/RemoveGroups`;

        return apiService.post(apiUrl, {
            Groups: groupsForRole.map(group => group.id)
        }).catch((error: AxiosResponse) => Promise.reject(error));
    }

    removeUsersFromRole(roleId: string, usersForRole: any[]): AxiosPromise {
        const apiUrl: string = `/api/directory/Roles(${roleId})/RemovePeople`;

        return apiService.post(apiUrl, {
            People: usersForRole.map(users => users.id)
        }).catch((error: AxiosResponse) => Promise.reject(error));
    }

    addUserToRole(roleId: string, People: any[]): AxiosPromise {
        return apiService.post(`/api/directory/Roles(${roleId})/AddPeople`, {
            People: People.map(user => user.id)
        }).catch((error: AxiosResponse) => Promise.reject(error));
    }

    addGroupToRole(roleId: string, Groups: any[]): AxiosPromise {
        return apiService.post(`/api/directory/Roles(${roleId})/AddGroups`, {
            Groups: Groups.map(group => group.id)
        }).catch((error: AxiosResponse) => Promise.reject(error));
    }

    getRoleMemberCount(roleIds: any): AxiosPromise {
        const data = {
            Roles: roleIds
        };

        return apiService
            .post('/api/directory/roles/memberscount', data)
            .then((response: AxiosResponse<IRoleMemberCount>) => {
                return response;
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getRole(roleId: string): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${roleId})`;

        return apiService.get(apiUrl)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getPeopleCountInRole(roleId: string): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${roleId})/UsersCount`;

        return apiService.get(apiUrl)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupCountInRole(roleId: string): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${roleId})/GroupsCount`;

        return apiService.get(apiUrl)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getRolesForOrganization(
        organizationId: string,
    ): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/GetUnassignedRoles?orgId=${organizationId}`;

        return apiService
        .get(apiUrl, {})
        .catch((error: AxiosResponse) => Promise.reject(error));
    }

    addRolesForOrganization(selectedItems: string[], organizationId: string): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/AddRoles?orgId=${organizationId}`;

        return apiService.post(apiUrl, {
            Roles: selectedItems
        }).catch((error: AxiosResponse) => Promise.reject(error));
    }
}

export const rolesService = new RolesService();
